import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getInvitationTokens, revokeInvitationToken } from '../../services/teacherService';
import { TeacherInvitationToken } from '../../types/teacher';

interface InvitationManagementDialogProps {
  open: boolean;
  onClose: () => void;
}

const InvitationManagementDialog: React.FC<InvitationManagementDialogProps> = ({
  open,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<TeacherInvitationToken[]>([]);
  const [revoking, setRevoking] = useState<number | null>(null);
  const [copiedTokenId, setCopiedTokenId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      fetchTokens();
    }
  }, [open]);

  const fetchTokens = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getInvitationTokens();
      // Sort by creation date, most recent first
      const sorted = data.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setTokens(sorted);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load invitation tokens';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (tokenId: number) => {
    setRevoking(tokenId);
    setError(null);
    try {
      await revokeInvitationToken(tokenId);
      await fetchTokens(); // Refresh the list
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to revoke invitation token';
      setError(errorMessage);
    } finally {
      setRevoking(null);
    }
  };

  const isTokenActive = (token: TeacherInvitationToken) => {
    return token.isActive && new Date(token.expiresAt) > new Date();
  };

  const getTokenStatus = (token: TeacherInvitationToken) => {
    if (!token.isActive) {
      return { label: 'Revoked', color: 'default' as const };
    }
    if (new Date(token.expiresAt) <= new Date()) {
      return { label: 'Expired', color: 'warning' as const };
    }
    return { label: 'Active', color: 'success' as const };
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCopyLink = async (token: TeacherInvitationToken) => {
    try {
      const invitationUrl = `${window.location.origin}/register?invite=${token.token}`;
      await navigator.clipboard.writeText(invitationUrl);
      setCopiedTokenId(token.tokenId);
      setTimeout(() => setCopiedTokenId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Manage Invitation Links</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : tokens.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No invitation links have been created yet
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="center">Uses</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokens.map((token) => {
                  const status = getTokenStatus(token);
                  return (
                    <TableRow key={token.tokenId}>
                      <TableCell>
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(token.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(token.expiresAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {token.timesUsed}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {token.lastUsedAt
                            ? formatDate(token.lastUsedAt)
                            : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          {isTokenActive(token) && (
                            <Tooltip title={copiedTokenId === token.tokenId ? 'Copied!' : 'Copy invitation link'}>
                              <IconButton
                                size="small"
                                color={copiedTokenId === token.tokenId ? 'success' : 'primary'}
                                onClick={() => handleCopyLink(token)}
                              >
                                {copiedTokenId === token.tokenId ? (
                                  <CheckCircleIcon />
                                ) : (
                                  <CopyIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          {isTokenActive(token) ? (
                            <Tooltip title="Revoke this invitation">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRevoke(token.tokenId)}
                                disabled={revoking === token.tokenId}
                              >
                                {revoking === token.tokenId ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <BlockIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Cannot revoke inactive token">
                              <span>
                                <IconButton size="small" disabled>
                                  <CancelIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && tokens.length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              Active tokens can be used by students to register under your class. Revoked or
              expired tokens cannot be used for new registrations.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvitationManagementDialog;
